import { DockviewApi, DockviewPanelApi } from "dockview-react";
import React from "react";
import SafeNumberInput from "./SafeNumberInput";
import { addFloatingPanel } from "./utils/DockerviewSettings";
import { ChartLauncherModal } from "./utils/ChartLauncherModal";
import { api } from "./utils/api";
import { ArticleViewer } from "./ArticleViewer";
import { RedditFeedTimeline } from "./RedditFeedTimeline";
import { HighlightedText, Mark } from "./utils/HighlightedText";

// Semantic search over the embedded tweets, articles and Reddit daily summaries (backend /correlate,
// rag_search table): the query is embedded and the closest tweets / article chunks / summaries are returned.

interface ITweetTickerChange {
  premarket_change_rel: number | null;
  regular_hours_change_rel: number | null;
}

interface ICorrelateTweet {
  kind: "tweet";
  url: string;
  source: string;
  published_at: number;
  similarity: number;
  content: string;
  content_marks: Mark[]; // full-text matches

  tickers: string[];
  ticker_changes: Record<string, ITweetTickerChange>;
}

interface ICorrelateArticle {
  kind: "article";
  url: string;
  source: string;
  published_at: number;
  similarity: number;
  title: string;
  title_marks: Mark[]; // full-text matches
  tickers: string; // space-separated
  ticker_changes: Record<string, number>; // US ticker -> regular/premarket change_rel (whichever session applies)
  matched_text: string; // the article chunk that matched (the article start when the title matched)
  matched_text_marks: Mark[];
  matched_title: boolean;
  chunk_index: number;
  chunk_count: number;
}

// A Reddit daily summary (reddit_events_summary); published_at = its generation time (23:59 NY of the day)
interface ICorrelateReddit {
  kind: "reddit";
  id: number;
  url: string; // "reddit-summary-<id>", only a unique key
  source: string;
  published_at: number;
  similarity: number;
  company: string;
  event: string;
  event_marks: Mark[];
  summary: string;
  summary_marks: Mark[];
  date: string;
}

type ICorrelateItem = ICorrelateTweet | ICorrelateArticle | ICorrelateReddit;

interface ICorrelateRequest {
  query: string;
  start_date: string;
  end_date: string;
  source: string;
  with_tickers: boolean;
  tickers_query: string;
  fts_query: string; // full-text filter, to_tsquery syntax as in Tweets / News search
  limit: number;
}

type SortKey = "similarity" | "time";

// Change indicator is jet black regardless of direction (as in Tweets Search).
const CHART_UP_COLOR = "#0a0a0a";
const CHART_DOWN_COLOR = "#0a0a0a";

function formatSignedPct(v: number): string {
  return `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`;
}

function isUsTicker(ticker: string): boolean {
  return ticker.length <= 5 && !ticker.includes(":") && !ticker.includes(".");
}

// Plotly hover text is HTML, so the item text is escaped and cut short
function timelineLabel(item: ICorrelateItem): string {
  const text =
    item.kind === "tweet" ? item.content : item.kind === "reddit" ? `${item.company} — ${item.event}` : item.title;
  const short = text.length > 90 ? text.slice(0, 90) + "..." : text;
  const escaped = short.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, " ");
  return `${item.source}: ${escaped}`;
}

function unixTimestampToNewYork(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  // convert to New York time, format as 2025-04-17 HH:MM (Thu)
  const newyorkDate: Date = new Date(
    date.toLocaleString("en-US", { timeZone: "America/New_York" }),
  );

  const day = String(newyorkDate.getDate()).padStart(2, "0");
  const month = String(newyorkDate.getMonth() + 1).padStart(2, "0");
  const year = newyorkDate.getFullYear();
  const hours = String(newyorkDate.getHours()).padStart(2, "0");
  const minutes = String(newyorkDate.getMinutes()).padStart(2, "0");
  const dayOfWeek = newyorkDate.toLocaleDateString("en-US", { weekday: "short" });

  return `${year}-${month}-${day} ${hours}:${minutes} (${dayOfWeek})`;
}

export const Correlate = (props: {
  api: DockviewPanelApi;
  containerApi: DockviewApi;
  initialQuery?: string; // e.g. a tweet opened from Tweets Search ("Open in correlate"); searched right away
}) => {
  const [items, setItems] = React.useState<ICorrelateItem[]>([]);
  const [request, setRequest] = React.useState<ICorrelateRequest>({
    query: props.initialQuery ?? "",
    start_date: "",
    end_date: "",
    source: "tweets",
    with_tickers: false,
    tickers_query: "",
    fts_query: "!earnings",
    limit: 100,
  });
  const [sortKey, setSortKey] = React.useState<SortKey>("similarity");
  const [reverse, setReverse] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [searched, setSearched] = React.useState(false);
  const [activeArticleUrl, setActiveArticleUrl] = React.useState<string>("");
  const [chartModalPublishedAt, setChartModalPublishedAt] = React.useState<number | null>(null);
  const [highlightedUrl, setHighlightedUrl] = React.useState<string | null>(null);

  // Timeline click: scroll to the item and highlight it briefly (as in the Reddit window)
  const scrollToItem = React.useCallback((id: number | string) => {
    const url = String(id);
    setHighlightedUrl(url);
    document.getElementById(url)?.scrollIntoView({ behavior: "smooth", block: "center" });
    window.setTimeout(() => {
      setHighlightedUrl((prev) => (prev === url ? null : prev));
    }, 2500);
  }, []);

  const sortedItems = React.useMemo(() => {
    const sorted = [...items].sort((a, b) =>
      sortKey === "similarity" ? b.similarity - a.similarity : b.published_at - a.published_at,
    );
    return reverse ? sorted.reverse() : sorted;
  }, [items, sortKey, reverse]);

  // search right away when opened with a query
  React.useEffect(() => {
    if (props.initialQuery) getResults();
  }, []);

  const getResults = async () => {
    if (!request.query.trim()) return;

    setErrorMessage("");
    setIsLoading(true);
    setSearched(true);

    try {
      const response = await api.post<{ items: ICorrelateItem[]; exact: boolean }>("/correlate", request);
      setItems(response.data.items);
    } catch (error: any) {
      console.error(error?.message || error);
      setErrorMessage("Error while searching");
    } finally {
      setIsLoading(false);
    }

    if (props.api) {
      const query = request.query.trim();
      props.api.setTitle("Correlate: " + (query.length > 15 ? query.slice(0, 15) + "..." : query));
    }
  };

  function openTickerChart(ticker: string) {
    if (!props.containerApi || !ticker) return;
    addFloatingPanel(props.containerApi, "realtime_chart", { ticker });
  }

  function handleChartOpen(ticker: string, startDate: string, endDate: string, interval: string) {
    addFloatingPanel(props.containerApi, "equity_chart", { ticker, startDate, endDate, interval });
  }

  return (
    <div className="relative flex flex-col h-full">
      <div className="flex-1 overflow-auto p-2">
        <form
          className="flex-col mb-4"
          onSubmit={(e) => {
            e.preventDefault();
            getResults();
          }}
        >
          <div className="form-group w-full">
            <label htmlFor="correlate_query">Query:</label>
            <input
              type="text"
              id="correlate_query"
              className="w-full"
              placeholder="e.g. DOJ probes fertilizer makers for price fixing"
              value={request.query}
              onChange={(e) => setRequest({ ...request, query: e.target.value })}
            />
          </div>

          <div className="flex gap-4">
            <div className="form-group">
              <label htmlFor="correlate_start_date">Start Date:</label>
              <input
                type="text"
                id="correlate_start_date"
                placeholder="YYYY-MM-DD"
                value={request.start_date}
                onChange={(e) => setRequest({ ...request, start_date: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label htmlFor="correlate_end_date">End Date:</label>
              <input
                type="text"
                id="correlate_end_date"
                placeholder="YYYY-MM-DD"
                value={request.end_date}
                onChange={(e) => setRequest({ ...request, end_date: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label htmlFor="correlate_source">Source:</label>
              <select
                id="correlate_source"
                value={request.source}
                onChange={(e) => setRequest({ ...request, source: e.target.value })}
                className="border border-gray-300 rounded p-1 block w-full"
              >
                <option value="">All</option>
                <option value="tweets">Tweets</option>
                <option value="reddit">Reddit summaries</option>
                <optgroup label="Articles">
                  <option value="bloomberg">Bloomberg</option>
                  <option value="reuters">Reuters</option>
                  <option value="wsj">WSJ</option>
                  <option value="marketwatch">MarketWatch</option>
                </optgroup>
              </select>
            </div>
          </div>

          <div className="flex gap-4 items-end">
            <div className="form-group">
              <label htmlFor="correlate_with_tickers">
                <input
                  type="checkbox"
                  id="correlate_with_tickers"
                  checked={request.with_tickers}
                  onChange={(e) => setRequest({ ...request, with_tickers: e.target.checked })}
                />
                &nbsp; With tickers
              </label>
            </div>

            <div className="form-group w-[200px]">
              <label htmlFor="correlate_fts_query">Full text Q:</label>
              <input
                type="text"
                id="correlate_fts_query"
                title="Tweet text / article title, e.g. deere & sued (as in Tweets and News search)"
                value={request.fts_query}
                onChange={(e) => setRequest({ ...request, fts_query: e.target.value })}
              />
            </div>

            <div className="form-group w-[200px]">
              <label htmlFor="correlate_tickers_query">Tickers Query:</label>
              <input
                type="text"
                id="correlate_tickers_query"
                value={request.tickers_query}
                onChange={(e) => setRequest({ ...request, tickers_query: e.target.value })}
              />
            </div>

            <div className="form-group w-[100px]">
              <label htmlFor="correlate_limit">Limit:</label>
              <SafeNumberInput
                id="correlate_limit"
                value={request.limit}
                onChange={(value) => setRequest({ ...request, limit: value })}
                min={1}
                max={500}
              />
            </div>

            <div className="form-group">
              <label htmlFor="correlate_sort">Sort by:</label>
              <select
                id="correlate_sort"
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value as SortKey)}
                className="border border-gray-300 rounded p-1 block w-full"
              >
                <option value="similarity">Similarity</option>
                <option value="time">Time published</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="correlate_reverse">
                <input
                  type="checkbox"
                  id="correlate_reverse"
                  checked={reverse}
                  onChange={(e) => setReverse(e.target.checked)}
                />
                &nbsp; Reverse
              </label>
            </div>

            <button type="submit" className="hidden">
              Search
            </button>
          </div>
        </form>

        {!isLoading && items.length > 0 && (
          <RedditFeedTimeline
            title="Published"
            colorTitle="Similarity"
            items={items.map((item) => ({
              id: item.url,
              generation_timestamp: new Date(item.published_at * 1000).toISOString(),
              label: timelineLabel(item),
              colorValue: item.similarity,
              detail: `sim ${item.similarity.toFixed(3)}`,
            }))}
            onItemClick={scrollToItem}
          />
        )}

        {isLoading ? (
          <div className="text-center">Loading...</div>
        ) : errorMessage !== "" ? (
          <p className="text-center">{errorMessage}</p>
        ) : sortedItems.length === 0 ? (
          <p className="text-center">{searched ? "Nothing found" : "Enter a query and press Enter"}</p>
        ) : (
          <div className="mb-20">
            {sortedItems.map((item) => (
              <div
                key={item.url}
                id={item.url}
                className={`flex gap-4 mb-4 ${highlightedUrl === item.url ? "bg-yellow-100" : ""}`}
              >
                <div>
                  <p className="text-gray-500 gap-4 flex">
                    <span>{unixTimestampToNewYork(item.published_at)}</span>
                    {item.kind === "reddit" ? (
                      <span>(reddit summary)</span>
                    ) : (
                      <a href={item.url} target="_blank" rel="noopener noreferrer">
                        {item.kind === "tweet" ? item.source : `(${item.source})`}
                      </a>
                    )}
                    {item.kind === "article" && (
                      <a
                        href="/"
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveArticleUrl(item.url);
                        }}
                      >
                        (text)
                      </a>
                    )}
                    <a
                      href="/"
                      onClick={(e) => {
                        e.preventDefault();
                        setChartModalPublishedAt(item.published_at);
                      }}
                    >
                      (chart)
                    </a>
                    <span className="tabular-nums" title="Cosine similarity to the query">
                      sim {item.similarity.toFixed(3)}
                    </span>
                  </p>

                  {item.kind === "tweet" ? (
                    <TweetBody item={item} openTickerChart={openTickerChart} />
                  ) : item.kind === "reddit" ? (
                    <RedditBody item={item} />
                  ) : (
                    <ArticleBody item={item} />
                  )}
                </div>
                <hr />
              </div>
            ))}
          </div>
        )}
      </div>

      {chartModalPublishedAt !== null && (
        <ChartLauncherModal
          publishedAt={chartModalPublishedAt}
          onSubmit={handleChartOpen}
          onClose={() => setChartModalPublishedAt(null)}
        />
      )}

      {activeArticleUrl !== "" && (
        <ArticleViewer url={activeArticleUrl} setActiveArticleUrl={setActiveArticleUrl} />
      )}
    </div>
  );
};

// Same layout as a row in Tweets Search
const TweetBody = (props: { item: ICorrelateTweet; openTickerChart: (ticker: string) => void }) => {
  const tweet = props.item;
  return (
    <>
      {tweet.tickers.length > 0 && (
        <p className="font-bold flex flex-wrap gap-x-1 items-baseline">
          {tweet.tickers.map((ticker) => {
            const c = tweet.ticker_changes[ticker];
            return (
              <span key={ticker} className="whitespace-nowrap">
                <button
                  type="button"
                  className="font-bold p-0 border-0 bg-transparent cursor-pointer hover:underline"
                  onClick={(e) => {
                    e.stopPropagation();
                    props.openTickerChart(ticker);
                  }}
                  title={`Open ${ticker} chart`}
                >
                  {ticker}
                </button>
                {c && c.premarket_change_rel !== null && c.regular_hours_change_rel !== null && (
                  <span className="ml-1 font-semibold tabular-nums">
                    (
                    <span style={{ color: c.premarket_change_rel >= 0 ? CHART_UP_COLOR : CHART_DOWN_COLOR }}>
                      {formatSignedPct(c.premarket_change_rel)}
                    </span>{" "}
                    <span style={{ color: c.regular_hours_change_rel >= 0 ? CHART_UP_COLOR : CHART_DOWN_COLOR }}>
                      {formatSignedPct(c.regular_hours_change_rel)}
                    </span>
                    )
                  </span>
                )}
              </span>
            );
          })}
        </p>
      )}
      <div className="whitespace-pre-wrap">
        <HighlightedText text={tweet.content} marks={tweet.content_marks} />
      </div>
    </>
  );
};

// Title (with tickers, as in News Search) + the matched part of the article
const ArticleBody = (props: { item: ICorrelateArticle }) => {
  const article = props.item;
  return (
    <>
      <div className="whitespace-pre-wrap prose prose-lg max-w-prose leading-relaxed font-sans">
        {article.tickers && (
          <mark className="ner-loc mr-1">
            {article.tickers.split(" ").map((t, i) => (
              <React.Fragment key={i}>
                {i > 0 && " "}
                {isUsTicker(t) ? <span className="font-semibold">{t}</span> : t}
                {article.ticker_changes?.[t] !== undefined && (
                  <span className="underline"> {article.ticker_changes[t].toFixed(1)}%</span>
                )}
              </React.Fragment>
            ))}
          </mark>
        )}
        <span className={article.matched_title ? "font-semibold" : ""}>
          <HighlightedText text={article.title} marks={article.title_marks} />
        </span>
      </div>
      {article.matched_text && (
        <div className="mt-1 border-l-2 border-gray-300 pl-2 text-gray-700 whitespace-pre-wrap max-w-prose">
          <span className="text-gray-500 text-xs">
            {article.matched_title
              ? "matched: title"
              : `matched: part ${article.chunk_index + 1}/${article.chunk_count}`}
          </span>
          <div>
            <HighlightedText text={article.matched_text} marks={article.matched_text_marks} />
          </div>
        </div>
      )}
    </>
  );
};

// Company — event (bold) + summary, as in the Reddit window's Summary tab
const RedditBody = (props: { item: ICorrelateReddit }) => {
  const r = props.item;
  return (
    <div className="max-w-prose">
      <p className="font-semibold">
        {r.company} — <HighlightedText text={r.event} marks={r.event_marks} />
        {r.date && <span className="font-normal text-gray-500"> ({r.date})</span>}
      </p>
      <div className="whitespace-pre-wrap text-gray-700">
        <HighlightedText text={r.summary} marks={r.summary_marks} />
      </div>
    </div>
  );
};
